<template>
  <div ref="elRef" :class="$style.wrapper">
    <HgButton
      type="button"
      theme="none"
      size="none"
      :class="$style.mobileButton"
      @click="toggleDropdown"
    >
      <HgIcon icon="Person" size="large" aria-hidden="true" />
    </HgButton>
    <div :class="[$style.emailWrapper, { [$style.activeWrapper]: !isMenuHidden }]">
      <template v-if="hasChildren">
        <HgButton
          type="button"
          theme="none"
          size="none"
          :themeClass="$style.linkIcon"
          :title="menuItem.label"
          :aria-controls="id"
          :aria-expanded="!isMenuHidden ? 'true' : 'false'"
          :class="[$style.link, $style.userLink]"
          @click="toggleDropdown"
        >
          <span :class="$style.linkBody">
            <span :class="$style.label">{{ menuItem.label }}</span>
            <span :class="[$style.dropDownIcon]">
              <HgIcon
                icon="ChevronDown"
                :rotation="isMenuHidden ? 0 : 180"
                size="large"
                aria-hidden="true"
              />
            </span>
          </span>
        </HgButton>
        <div :id="id" ref="dropdownList" :hidden="isMenuHidden" :class="$style.dropdown">
          <HgLink
            v-for="item in (menuItem as DropdownMenuItem).children"
            :key="item.label"
            :tag="(item as RouterLinkMenuItem).to ? 'router-link' : 'a'"
            :to="(item as RouterLinkMenuItem).to"
            :activeClass="(item as RouterLinkMenuItem).to && $style.menuActiveLink"
            :class="$style.link"
            @click="emitDropdownMenuItemClickEvent(item, $event)"
          >
            <HgIcon v-if="item.icon" :icon="item.icon" size="medium" theme="current" />
            <span :class="$style.itemLabel">{{ item.label }}</span>
          </HgLink>
        </div>
      </template>
      <HgLink
        v-else
        :title="menuItem.label"
        :data-menu-item-badge="menuItem.badge"
        :tag="(menuItem as RouterLinkMenuItem).to ? 'router-link' : 'a'"
        :to="(menuItem as RouterLinkMenuItem).to"
        :activeClass="(menuItem as RouterLinkMenuItem).to && $style.activeLink"
        :icon="menuItem.icon"
        :themeClass="$style.linkIcon"
        :class="[
          $style.link,
          $style.linkStatic,
          {
            [$style.bordered]: bordered,
            [$style.badge]: !!menuItem.badge,
            [$style.hasIcon]: menuItem.icon,
          },
        ]"
      >
        <span :class="$style.linkBody">
          <span :class="$style.label">
            {{ menuItem.label }}
          </span>
        </span>
      </HgLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDropdownHandler } from '@/components/Agency/AgencySwitcher/mixins/useDropdownHandler';
import { HgButton, HgIcon, HgLink, Icon, type MenuItem } from '@smg-real-estate/domus';
import { type DropdownMenuItem, type RouterLinkMenuItem } from '@smg-real-estate/domus/internal';
import { computed } from 'vue';

const { elRef, isMenuHidden, closeDropdown, toggleDropdown } = useDropdownHandler();

const { id, menuItem, bordered } = defineProps({
  /**
   * Id used for aria controls.
   */
  id: {
    type: String,
    default: null,
  },
  /**
   * The menu object comprising of href, label, and optional children.
   */
  menuItem: {
    type: Object as () => MenuItem & { badge?: string; icon?: Icon },
    required: true,
  },
  /**
   * Styled with border.
   */
  bordered: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['dropDownMenuItemClick']);

const hasChildren = computed<boolean>(() => (menuItem as DropdownMenuItem).children.length > 0);

const emitDropdownMenuItemClickEvent = (item: MenuItem, event: Event) => {
  closeDropdown();

  /**
   * Triggered when the link was clicked
   */
  emit('dropDownMenuItemClick', item, event);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$truncate-text-max-width: 320px;
$space-120: 1.2rem;
$dropdown-menu-height: 112px;

.activeWrapper {
  background-color: $color-deep-blue-30;
}

.wrapper {
  display: flex;
}

.mobileButton {
  display: flex;
  align-items: center;

  @media (min-width: $viewport-sm-min) {
    display: none;
  }
}

.emailWrapper {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  height: 35px;
  color: $color-deep-blue-100;
  border-radius: $border-radius-l;

  &:hover {
    background-color: $color-deep-blue-30;
  }

  .linkIcon {
    @include color-icon($theme-color-dark);

    @media (max-width: $viewport-sm-min - 1) {
      margin: 0;
    }
  }

  .linkBody {
    display: flex;
    align-items: center;
    padding: 0 $space-50;

    .label {
      @include truncate-text;

      & {
        max-width: $truncate-text-max-width;
      }
    }
  }

  .dropDownIcon {
    display: flex;
    margin-left: $space-25;
  }

  .hasIcon {
    .linkBody {
      @include visually-hidden;
    }

    .dropDownIcon {
      display: none;
    }
  }

  .userLink {
    display: none;
  }

  .linkStatic {
    &.hasIcon {
      .linkIcon + span {
        margin-left: 0;
      }
    }
  }

  .dropdown[hidden] {
    display: none !important;
  }

  .dropdown {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    height: $dropdown-menu-height;
    margin: 0;
    margin-bottom: -120px;
    background-color: #fff;
    border-radius: $border-radius-l;
    box-shadow: 0 6px 24px 0 rgb(0 0 0 / 0.32);

    .link {
      @include body-2($color-grey-120);

      & {
        height: $dropdown-menu-height;
        padding: 0 $space-75;
        font-size: 1rem;
        line-height: $space-150;
        border: none;
      }

      &:hover {
        text-decoration: none;
        background-color: $color-deep-blue-30;

        &:first-child {
          border-radius: $border-radius-l $border-radius-l 0 0;
        }

        &:last-child {
          border-radius: 0 0 $border-radius-l $border-radius-l;
        }
      }

      .itemLabel {
        padding: 0 20px;
        margin-right: 0.5rem;
      }
    }
  }

  @media (max-width: $viewport-sm-min - 1) {
    .dropdown {
      &.left {
        transform: translate(-10%, 100%);

        &::before {
          left: 10%;
        }
      }

      &.right {
        transform: translate(-90%, 100%);

        &::before {
          left: 90%;
        }
      }
    }
  }

  @media (min-width: $viewport-sm-min) {
    .hasIcon {
      .linkBody {
        @include reverse-visually-hidden;
      }

      .dropDownIcon {
        display: block;
      }
    }

    .userLink {
      display: flex;
    }
  }

  .icon {
    margin-right: $space-25;

    .rightIcon & {
      margin-right: 0;
      margin-left: $space-25;
    }
  }

  @media (min-width: $viewport-sm-min) {
    .linkStatic {
      &.hasIcon {
        .linkIcon + span {
          margin-left: $space-50;
        }
      }
    }
  }
}
</style>
